/**
 * JWT clock helpers (permanent-session-continuity design §1.2, §4.6, §4.9).
 *
 * Skew convention: skewMs = localReceiveMs - iat*1000 (positive when the device
 * clock is ahead). serverNowMs ≈ Date.now() - skewMs. Skew is recorded ONLY from
 * freshly received tokens (login / refresh success), never from rehydrated ones.
 */

export const PROACTIVE_LEAD_MS = 5 * 60_000;
export const RESUME_WINDOW_MS = 60 * 60_000;
export const MIN_PROACTIVE_INTERVAL_MS = 60_000;
export const MIN_TIMER_DELAY_MS = 30_000;
export const MAX_TIMER_DELAY_MS = 6 * 3_600_000;
export const REFRESH_EXPIRY_TOLERANCE_MS = 60_000; // mirrors the server's clockTolerance (FR-3a)

const SKEW_STORAGE_KEY = 'accessTokenClockSkewMs';

/** Base64url-safe JWT payload decode. Returns null on any malformed input; never throws. */
export function decodeJwtPayload(token) {
  try {
    if (typeof token !== 'string' || !token) return null;
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad === 1) return null;
    if (pad) b64 += '='.repeat(4 - pad);
    const binary = atob(b64);
    let json;
    try {
      // Decode UTF-8 safely (claims may contain non-ASCII names).
      json = decodeURIComponent(
        Array.prototype.map
          .call(binary, (ch) => '%' + ('00' + ch.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
    } catch (utfErr) {
      json = binary;
    }
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
  } catch (e) {
    return null;
  }
}

/** Record skew from a freshly received access token. Never throws. */
export function recordFreshAccessToken(token, receivedAtMs = Date.now()) {
  try {
    const iat = decodeJwtPayload(token)?.iat;
    if (typeof iat !== 'number' || !Number.isFinite(iat)) return;
    const received = Number.isFinite(receivedAtMs) ? receivedAtMs : Date.now();
    const skew = received - iat * 1000;
    if (!Number.isFinite(skew)) return;
    localStorage.setItem(SKEW_STORAGE_KEY, String(Math.round(skew)));
  } catch (e) {
    console.warn('[jwtClock] recordFreshAccessToken failed:', e?.message || e);
  }
}

/** Last measured skew in ms (0 when unknown). Never throws. */
export function getClockSkewMs() {
  try {
    const raw = localStorage.getItem(SKEW_STORAGE_KEY);
    if (raw === null || raw === undefined || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch (e) {
    console.warn('[jwtClock] getClockSkewMs failed:', e?.message || e);
    return 0;
  }
}

/** max(0, expMs - leadMs - serverNow), or null when expMs is not finite. */
export function computeProactiveRefreshDelayMs({ expMs, nowMs, skewMs, leadMs } = {}) {
  if (typeof expMs !== 'number' || !Number.isFinite(expMs)) return null;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const skew = Number.isFinite(skewMs) ? skewMs : 0;
  const lead = Number.isFinite(leadMs) ? leadMs : PROACTIVE_LEAD_MS;
  return Math.max(0, expMs - lead - (now - skew));
}

/** (nowMs - skewMs) >= expMs - windowMs. False when expMs is not finite. */
export function isWithinRefreshWindow({ expMs, nowMs, skewMs, windowMs } = {}) {
  if (typeof expMs !== 'number' || !Number.isFinite(expMs)) return false;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const skew = Number.isFinite(skewMs) ? skewMs : 0;
  const win = Number.isFinite(windowMs) ? windowMs : RESUME_WINDOW_MS;
  return now - skew >= expMs - win;
}

/** FR-10.2 (design §4.9, normative). */
export function isRefreshTokenExpired(refreshToken, nowMs, skewMs) {
  try {
    const exp = decodeJwtPayload(refreshToken)?.exp; // existing decoder; null on any malformed input
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return false; // undecodable / no exp → NOT expired
    const now = Number.isFinite(nowMs) ? nowMs : Date.now();
    const skew = Number.isFinite(skewMs) ? skewMs : 0; // skew = local − server (FR-6.1)
    return exp * 1000 + REFRESH_EXPIRY_TOLERANCE_MS < now - skew;
  } catch (e) {
    console.warn('[jwtClock] isRefreshTokenExpired failed:', e?.message || e);
    return false; // never a reason to log out on its own
  }
}
